/* eslint-disable max-len */
import React, {
  useState, useEffect, useCallback, useRef,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import cookies from '../../utils/cookies'
import getPeriod from '../../utils/getPeriod'
import PlayerApi from '../../utils/playerApi'
import { checkConcurrentStream } from '../../actions/concurrentStreamActions'
import {
  LIVE_RESTART_EVENT,
  LIVE_RESTART_ERROR,
  LIVE_SKIP_EVENT,
  LIVE_STREAM_ERROR,
  LIVE_RESUME_EVENT,
  NOT_LOGGED_IN,
  RESTART_DISABLED_STREAM_DIALOG,
  RESTART_STREAM_DIALOG,
  RESTART_STREAM_END,
  PLAYER_HAS_STARTED,
  PLAYER_STREAM_END,
  PLAYER_RESTART_STREAM_HAS_STARTED,
  UPDATE_PROGRAMME_METADATA,
  STV_COOKIES,
  FEATURE_LIVE_SCRUB_SKIP_BUTTON,
} from '../../constants'

const playerApi = PlayerApi()
const { STV_AUTH_REQUIRED } = STV_COOKIES

const withLiveEvents = (WrappedComponent) => {
  const WithLiveEvents = (props) => {
    const { playbackUrl, channel: concurrentChannel } = useSelector(state => state.concurrentStream)
    const dispatch = useDispatch()
    const [state, setState] = useState({
      error: false,
      isRestartStream: false,
      isScrubStream: false,
      playerHasStarted: false,
      lastUpdated: Date.now(),
      liveRestartStreamHasStarted: false,
      poster: props.poster,
    })

    const prevPropsRef = useRef(props)

    const updateState = useCallback((newState) => {
      setState(prevState => ({ ...prevState, ...newState }))
    }, [])

    const getStreamUrl = useCallback(() => {
      if (props.channel !== 'stv-plus-1' && state.isRestartStream) {
        return props.restartUrl
      }

      if (playbackUrl && (props.channel === concurrentChannel)) {
        return playbackUrl
      }

      return props.streamUrl
    }, [props.channel, props.restartUrl, props.streamUrl, state.isRestartStream, playbackUrl, concurrentChannel])

    const buildSumoObject = useCallback(results => ({
      guid: results?.guid,
      channel: results?.channel,
      videoId: results?.videoId,
      programme: {
        guid: results?.now?.programme?.guid,
        name: results?.now?.programme?.name,
      },
    }), [])
    // eslint-disable-next-line consistent-return
    const updateSchedule = useCallback(() => {
      if (state.liveRestartStreamHasStarted || !props.endTime) return false

      const minPeriod = 60000
      const period = getPeriod({ time: props.endTime })

      const getNextProgramme = () => {
        playerApi({
          path: `/streams/${props.channel}`,
          query: { groupToken: props.groupToken },
          dispatch: props.dispatch,
          sendAuthHeader: cookies.get(STV_AUTH_REQUIRED) === 'true',
        })
          .then((response) => {
            const results = response?.results
            if (!results) return
            const newEndTime = results?.now?.endTime
            const newProgrammeTitle = results?.now?.title
            const programmeChanged = props.title === newProgrammeTitle
            let newPeriod

            if (programmeChanged) {
              newPeriod = getPeriod({ time: newEndTime })
              window.postMessage({
                type: UPDATE_PROGRAMME_METADATA,
                body: buildSumoObject(results),
              }, '*')
            } else {
              newPeriod = minPeriod
            }
            setTimeout(getNextProgramme, newPeriod)
          })
      }
      setTimeout(getNextProgramme, period)
    }, [state.liveRestartStreamHasStarted, props.endTime, props.channel, props.groupToken, props.dispatch, props.title, buildSumoObject])

    const handleIframeMessage = useCallback((message) => {
      const { type } = message?.data

      if (type === LIVE_RESTART_EVENT) {
        cookies.set('playbackOption', 'restart')
        handleRestartOrResumeEvent(type)
      }

      if (FEATURE_LIVE_SCRUB_SKIP_BUTTON && type === LIVE_SKIP_EVENT) {
        cookies.set('playbackOption', 'scrub')
        handleSkipScrubEvent()
      }

      if (type === LIVE_RESUME_EVENT) {
        cookies.set('playbackOption', 'resume')
        handleRestartOrResumeEvent(type)
      }

      if (type === PLAYER_HAS_STARTED) {
        updateState({ playerHasStarted: true })
      }
      if (type === PLAYER_STREAM_END) {
        handleLiveStreamEnd()
      }
      if (type === PLAYER_RESTART_STREAM_HAS_STARTED) {
        updateState({ liveRestartStreamHasStarted: true })
      }

      if (type === RESTART_STREAM_END) {
        updateState({ poster: null })
        handleLiveRestartStreamEnd()
      }

      if (type === LIVE_RESTART_ERROR || type === LIVE_STREAM_ERROR) {
        updateState({ error: type })
      }
    }, [updateState])

    const handleSkipScrubEvent = () => {
      if (!FEATURE_LIVE_SCRUB_SKIP_BUTTON) return
      updateState({
        error: false,
        isRestartStream: true,
        isScrubStream: true,
        lastUpdated: Date.now(),
      })
    }

    const handleRestartOrResumeEvent = (eventType) => {
      updateState({
        error: false,
        isRestartStream: eventType === LIVE_RESTART_EVENT,
        isScrubStream: false,
        lastUpdated: Date.now(),
      })
    }

    const handleLiveStreamEnd = () => {
      dispatch(checkConcurrentStream(true, props.channel, false, false))
    }

    const handleLiveRestartStreamEnd = () => {
      cookies.set('playbackOption', 'resume eop')
      updateState({
        error: RESTART_STREAM_END,
        isRestartStream: false,
        isScrubStream: false,
      })
    }

    const handleLiveStreamStarted = useCallback(() => {
      const loginDisabled = !props.loginEnabled
      if (props.isLoggedIn || loginDisabled) {
        updateState({ error: null })
      } else {
        updateState({ error: NOT_LOGGED_IN })
      }
    }, [props.isLoggedIn, props.loginEnabled])

    const handleFromStartStreamStarted = useCallback(() => {
      const loginDisabled = !props.loginEnabled
      if (props.isLoggedIn || loginDisabled) {
        window.parent.postMessage({
          type: LIVE_RESTART_EVENT,
        }, '*')
        updateState({ error: null })
      } else {
        updateState({ error: NOT_LOGGED_IN })
      }
    }, [props.isLoggedIn, props.loginEnabled])

    const handleHomeClicked = () => {
      window.location.href = '/'
    }

    useEffect(() => {
      const {
        isLoggedIn,
        restartEnabled,
        loginChecked,
        loginEnabled,
      } = props

      if (!state.playerHasStarted && restartEnabled && !isLoggedIn && loginChecked && loginEnabled) {
        updateState({ error: RESTART_STREAM_DIALOG })
      } else if (!state.playerHasStarted && !restartEnabled && !isLoggedIn && loginChecked && loginEnabled) {
        updateState({ error: RESTART_DISABLED_STREAM_DIALOG })
      }
      cookies.set('playbackOption', 'manual')
      localStorage.removeItem('scrubbingToggled')

      window.addEventListener('message', handleIframeMessage)
      updateSchedule()

      return () => {
        window.removeEventListener('message', handleIframeMessage)
      }
    }, [props, state.playerHasStarted, handleIframeMessage, updateSchedule])

    useEffect(() => {
      const prevProps = prevPropsRef.current
      if ((props.channel !== prevProps.channel) && !props.isLoggedIn && props.loginEnabled) {
        updateState({
          playerHasStarted: false,
          isRestartStream: false,
          isScrubStream: FEATURE_LIVE_SCRUB_SKIP_BUTTON ? false : state.isScrubStream,
          error: props.restartEnabled ? RESTART_STREAM_DIALOG : RESTART_DISABLED_STREAM_DIALOG,
        })
      }

      if (!state.playerHasStarted && !props.isLoggedIn && props.loginEnabled) {
        if (!prevProps.restartEnabled && props.restartEnabled) {
          // if channel changes, reset isRestartStream to false
          updateState({ error: RESTART_STREAM_DIALOG })
        } else if (!props.restartEnabled && prevProps.restartEnabled) {
          if (state.error === RESTART_STREAM_DIALOG) {
            // if we're showing the live restart dialog and
            // live restart is no longer available,remove the dialog
            updateState({ error: RESTART_DISABLED_STREAM_DIALOG })
          } else {
            // we're not showing the dialog and not playig, so make sure we don't start
            // a restart stream. This resets the player iframe src.
            updateState({ isRestartStream: false })
          }
        }
      } else if (!prevProps.restartEnabled && props.restartEnabled && !props.isLoggedIn && props.loginEnabled) {
        // if player hasn't started and live restart is enabled, show live restart dialog
        updateState({ error: RESTART_STREAM_DIALOG })
      }

      prevPropsRef.current = props
    }, [props, state, updateState])

    return (
      <WrappedComponent
        {...props}
        poster={state.poster || props.poster}
        key={state.lastUpdated}
        isRestartStream={state.isRestartStream}
        isScrubStream={state.isScrubStream}
        error={props.error || state.error}
        streamUrl={getStreamUrl()}
        onRestartClick={() => handleRestartOrResumeEvent(LIVE_RESTART_EVENT)}
        onLiveStreamClicked={handleLiveStreamStarted}
        onHomeClicked={handleHomeClicked}
        onFromStartStreamClicked={handleFromStartStreamStarted}
      />
    )
  }

  WithLiveEvents.propTypes = {
    poster: PropTypes.string,
    restartUrl: PropTypes.string,
    streamUrl: PropTypes.string.isRequired,
    channel: PropTypes.string,
    matId: PropTypes.string,
    groupToken: PropTypes.string,
    dispatch: PropTypes.func.isRequired,
    restartEnabled: PropTypes.bool,
    scrubEnabled: PropTypes.bool,
    endTime: PropTypes.string,
    error: PropTypes.string,
    title: PropTypes.string.isRequired,
    loginEnabled: PropTypes.bool.isRequired,
    isLoggedIn: PropTypes.bool.isRequired,
    loginChecked: PropTypes.bool.isRequired,
    prevChannel: PropTypes.string,
    prevRestartEnabled: PropTypes.bool,
  }

  WithLiveEvents.defaultProps = {
    poster: null,
    restartUrl: null,
    channel: 'stv',
    matId: 'unknown',
    restartEnabled: false,
    scrubEnabled: false,
    endTime: null,
    error: null,
    groupToken: null,
    prevChannel: null,
    prevRestartEnabled: false,
  }

  return WithLiveEvents
}

export default withLiveEvents
